import { getCollection, addDocToCollection, updateDocById, deleteDocByCollectionAndId } from '../_shared/firestore/node/index.js';
import { createCMMSView } from '../views/views.js';
import { serverTimestamp } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';

// Read technical templates (top-level collection with company_id)
async function getTechnicalTemplatesByCompany(db, companyId, category = 'GMP') {
  try {
    const whereConfig = [
      { key: 'company_id', operator: '==', value: companyId },
      ...(category ? [{ key: 'category', operator: '==', value: category }] : [])
    ];
    const docs = await getCollection(db, 'technical_templates', whereConfig);
    const items = (docs || []).map(doc => ({ id: doc.$id, ...doc }));
    return createCMMSView({ data: items });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Create a technical audit (top-level collection with company_id)
async function createTechnicalAudit(db, companyId, data) {
  const buildPayload = () => {
    try {
      const auth = getAuth(db.app);
      const user = auth?.currentUser;
      const payload = {
        ...data,
        company_id: companyId,
        created_by: user?.uid || '',
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      };
      if (data?.status === 'completed') {
        payload.submitted_at = serverTimestamp();
      }
      return payload;
    } catch {
      // Fallback if auth not available
      const payload = { ...data, company_id: companyId, created_at: serverTimestamp(), updated_at: serverTimestamp() };
      if (data?.status === 'completed') payload.submitted_at = serverTimestamp();
      return payload;
    }
  };
  const payload = buildPayload();
  try {
    const ref = await addDocToCollection(db, 'technical_audits', payload);
    return createCMMSView({ data: { id: ref.id, ...payload } });
  } catch (error) {
    // Fallback to legacy subcollection path if top-level write is denied
    try {
      const subPath = `companies/${companyId}/technical_audits`;
      const ref = await addDocToCollection(db, subPath, payload);
      return createCMMSView({ data: { id: ref.id, ...payload } });
    } catch (error2) {
      return createCMMSView({ error: error2 || error });
    }
  }
}

export {
  getTechnicalTemplatesByCompany,
  createTechnicalAudit,
};

// Template create/update/delete (top-level collection with company_id)
async function createTechnicalTemplate(db, payload) {
  const doc = {
    ...payload,
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  };
  const ref = await addDocToCollection(db, 'technical_templates', doc);
  return createCMMSView({ data: { id: ref.id, ...doc } });
}

async function updateTechnicalTemplate(db, id, payload) {
  const patch = { ...payload, updated_at: serverTimestamp() };
  await updateDocById(db, 'technical_templates', id, patch);
  return createCMMSView({ data: { id, ...patch } });
}

async function deleteTechnicalTemplate(db, companyId, id) {
  await deleteDocByCollectionAndId(db, 'technical_templates', id);
  return createCMMSView({ data: { id, deleted: true } });
}

export { createTechnicalTemplate, updateTechnicalTemplate, deleteTechnicalTemplate };

// List technical audits (top-level collection with company_id)
async function getTechnicalAuditsByCompany(db, companyId, opts = {}) {
  try {
    const whereConfig = [{ key: 'company_id', operator: '==', value: companyId }];
    if (opts.status) whereConfig.push({ key: 'status', operator: '==', value: opts.status });
    if (opts.template_id) whereConfig.push({ key: 'template_id', operator: '==', value: opts.template_id });
    let docs = await getCollection(db, 'technical_audits', whereConfig);
    // Backward-compatibility: check company subcollection if top-level empty
    if (!docs || docs.length === 0) {
      const subPath = `companies/${companyId}/technical_audits`;
      docs = await getCollection(db, subPath, opts.status ? { key: 'status', operator: '==', value: opts.status } : undefined);
      // Note: subcollection may not store template_id; best-effort only
      if (opts.template_id) {
        docs = (docs || []).filter(d => d.template_id === opts.template_id);
      }
    }
    const items = (docs || []).map(doc => ({ id: doc.$id, ...doc }));
    return createCMMSView({ data: items });
  } catch (error) {
    return createCMMSView({ error });
  }
}

export { getTechnicalAuditsByCompany };

// Update/delete technical audits (top-level collection)
async function updateTechnicalAudit(db, id, payload) {
  try {
    const patch = { ...payload, updated_at: serverTimestamp() };
    if (payload?.status === 'completed') {
      patch.submitted_at = patch.submitted_at || serverTimestamp();
    }
    await updateDocById(db, 'technical_audits', id, patch);
    return createCMMSView({ data: { id, ...patch } });
  } catch (error) {
    return createCMMSView({ error });
  }
}

async function deleteTechnicalAudit(db, id) {
  try {
    await deleteDocByCollectionAndId(db, 'technical_audits', id);
    return createCMMSView({ data: { id, deleted: true } });
  } catch (error) {
    return createCMMSView({ error });
  }
}

export { updateTechnicalAudit, deleteTechnicalAudit };
